import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import moment from "moment";
import { IBriefNote } from "../constants";
import { useBriefNotes } from "./useBriefNotes";
import { BriefNoteItem, briefNoteElementId } from "./BriefNoteItem";
import { BriefNoteComposer } from "./BriefNoteComposer";
import { briefNoteTaggableUsers } from "../../../api/endpoints";
import { useScrollAwayFooter } from "../../../hooks/useScrollAwayFooter";
import { useUser } from "../../../hooks/useUser";
import { IconChatMsgs } from "../../../utils/icons";
import { DotLottleLoader } from "../../Loader";

interface Props {
    projectId?: number;
    /** Unset while the brief is being created: the thread opens once it is saved. */
    briefId?: number;
    /** Scroll to and highlight this note once the thread loads. */
    focusNoteId?: number | string | null;
    /** Pane title; the narrow-screen tabs already say "Notes". */
    showHeader?: boolean;
}

const sameId = (a: unknown, b: unknown) => a != null && b != null && String(a) === String(b);

const EmptyState: FC<{ text: string }> = ({ text }) => (
    <Box className="flex-1 w-full flex flex-col items-center justify-center gap-6 px-6 py-10 text-center">
        <IconChatMsgs />
        <Typography className="text-text-2">{text}</Typography>
    </Box>
);

/**
 * The brief's internal notes thread (internal viewers only — BriefDialog
 * never mounts it for brand users) (copied from the video-review CommentsListPanel):
 * day-grouped cards in a scroll list, with the composer pinned over the bottom
 * of the pane. The composer scrolls away with the list (useScrollAwayFooter).
 */
export const BriefNotesPanel: FC<Props> = ({ projectId, briefId, focusNoteId, showHeader = true }) => {
    const { user } = useUser();
    const { notes, loading, saving, createNote, updateNote, deleteNote } = useBriefNotes(projectId, briefId);

    const hasBrief = !!projectId && !!briefId;
    const { listRef, footerRef, contentRef } = useScrollAwayFooter(hasBrief);

    // Grouped by the viewer's LOCAL day, in thread order.
    const groups = useMemo(() => {
        const byDay: { day: string; label: string; notes: IBriefNote[] }[] = [];
        notes.forEach((note) => {
            if (!note.created_at) return;
            const created = moment(note.created_at);
            const day = created.format("YYYY-MM-DD");
            const last = byDay[byDay.length - 1];
            if (last?.day === day) last.notes.push(note);
            else byDay.push({ day, label: created.format("MMM DD, YYYY"), notes: [note] });
        });
        return byDay;
    }, [notes]);

    const scrollToBottom = (behavior: ScrollBehavior) => {
        const list = listRef.current;
        if (list) list.scrollTo({ top: list.scrollHeight, behavior });
    };

    // Once the thread loads: focus the deep-linked note, else open at the
    // newest note. The short delay lets the dialog lay out (and the footer
    // hook's bottom-pinning settle) before centring the focused note.
    const wasLoadingRef = useRef(loading);
    const hasFocusedRef = useRef(false);
    useEffect(() => {
        const justLoaded = wasLoadingRef.current && !loading;
        wasLoadingRef.current = loading;
        if (!justLoaded) return undefined;

        const timer = window.setTimeout(() => {
            const list = listRef.current;
            if (!list) return;
            const target =
                focusNoteId != null && !hasFocusedRef.current
                    ? document.getElementById(briefNoteElementId(focusNoteId))
                    : null;
            if (!target) {
                scrollToBottom("auto");
                return;
            }
            hasFocusedRef.current = true;
            // Scroll the list only — scrollIntoView would also scroll the
            // dialog's overflow-hidden wrappers.
            const listRect = list.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            list.scrollTo({
                top: list.scrollTop + (targetRect.top - listRect.top) - (list.clientHeight - targetRect.height) / 2,
                behavior: "smooth"
            });
            target.focus({ preventScroll: true });
        }, 300);
        return () => window.clearTimeout(timer);
    }, [loading, focusNoteId]);

    // After the viewer posts, show their note at the bottom.
    const [postedCount, setPostedCount] = useState(0);
    useEffect(() => {
        if (postedCount) scrollToBottom("smooth");
    }, [postedCount]);

    const handleCreate = async (message: string) => {
        const ok = await createNote(message);
        if (ok) setPostedCount((count) => count + 1);
        return ok;
    };

    let body: React.ReactNode;
    if (!hasBrief) {
        body = <EmptyState text="Notes open once this brief is saved." />;
    } else if (loading) {
        body = (
            <Box className="flex-1 flex items-center justify-center">
                <DotLottleLoader fill={false} sx={{ width: 160, height: 160 }} />
            </Box>
        );
    } else if (!groups.length) {
        body = <EmptyState text="There are no notes yet" />;
    } else {
        body = groups.map((group, index) => (
            <React.Fragment key={group.day}>
                {index !== 0 && <Divider />}
                <Box className="flex flex-col w-full p-4 gap-2">
                    <Box className="p-2 flex items-center justify-center">
                        <Typography variant="body2" className="!text-xs text-text-2">
                            {group.label}
                        </Typography>
                    </Box>
                    {group.notes.map((note) => {
                        const isMine = !!note.userid && sameId(note.userid, user?.userid);
                        return (
                            <BriefNoteItem
                                key={note.id}
                                note={note}
                                canManage={isMine && !note.deleted}
                                focused={sameId(note.id, focusNoteId)}
                                taggableUrl={
                                    projectId && briefId
                                        ? briefNoteTaggableUsers(projectId, briefId)
                                        : null
                                }
                                onUpdate={updateNote}
                                onDelete={deleteNote}
                            />
                        );
                    })}
                </Box>
            </React.Fragment>
        ));
    }

    return (
        <Box className="flex flex-col flex-1 w-full h-full min-h-0 bg-surface-2">
            {showHeader && (
                <Box className="shrink-0 px-4 py-4 border-b border-border-token bg-surface">
                    <Typography variant="h6" className="!font-semibold">
                        Notes
                    </Typography>
                </Box>
            )}
            {/* The pane clips; the list scrolls; the composer floats over the
                list's bottom padding (kept equal to its height by the hook).
                overflow: clip, not hidden: a hidden wrapper is still a scroll
                container, and focusing or typing in the scrolled-away composer
                would scroll it and shift the whole list. */}
            <Box className="relative flex-1 min-h-0 overflow-hidden supports-[overflow:clip]:overflow-clip">
                <Box ref={listRef} className="absolute inset-0 overflow-y-auto overscroll-contain dialog-content">
                    <Box ref={contentRef} className="min-h-full flex flex-col">
                        {body}
                    </Box>
                </Box>
                {hasBrief && (
                    <Box
                        ref={footerRef}
                        className="absolute inset-x-0 bottom-0 border-t border-border-token bg-surface px-4 py-3 will-change-transform"
                    >
                        <BriefNoteComposer
                            saving={saving}
                            disabled={loading}
                            projectId={projectId}
                            briefId={briefId}
                            onSubmit={handleCreate}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
